import { ReactElement } from 'react';
import { InfoIcon, PhoneIcon } from '../../../assets/icons';
import { SelectableCard } from '../../../core/components';
import { SUPPORT_PHONE_NUMBER } from '../../../core/constants';
import { tokens } from '../../../core/theme';
import { REQUEST_TYPE_CONFIG, REQUEST_TYPES, RequestType } from '../../../core/types';
import { useBranchFromQuery } from '../hooks';
import { useIsDesktop, usePageTitle } from '../../../core/hooks';

type HomeCategoryType = Exclude<RequestType, 'informacao'>;

const CATEGORY_DESCRIPTIONS: Record<HomeCategoryType, string> = {
  elogio: 'Compartilhe uma experiência positiva',
  sugestao: 'Apresente sugestões de melhoria',
  reclamacao: 'Relate um problema ou insatisfação',
};

const CATEGORIES = REQUEST_TYPES.filter(
  (type): type is HomeCategoryType => type !== 'informacao',
).map((type) => ({
  type,
  icon: REQUEST_TYPE_CONFIG[type].icon,
  title: REQUEST_TYPE_CONFIG[type].label,
  description: CATEGORY_DESCRIPTIONS[type],
}));

export const HomePage = (): ReactElement => {
  const isDesktop = useIsDesktop();
  useBranchFromQuery();
  usePageTitle('Home');

  return (
    <div style={styles.container}>
      <section style={styles.categorySection}>
        <h1 style={styles.heading}>Como podemos te ajudar?</h1>
        <div style={{ ...styles.categoryList, flexDirection: isDesktop ? 'row' : 'column' }}>
          {CATEGORIES.map((category) => (
            <SelectableCard
              key={category.type}
              variant="link"
              icon={category.icon}
              title={category.title}
              description={category.description}
              to={{ pathname: '/support', search: `tipo=${category.type}` }}
            />
          ))}
          <SelectableCard
            variant="link"
            icon={InfoIcon}
            title="Informação"
            description="Não achou o que precisa? Fale com a gente."
            to={'/faq'}
          />
        </div>
      </section>
      <section
        style={{ ...styles.contactContainer, ...(isDesktop ? styles.contactContainerDesktop : {}) }}
      >
        <h2 style={styles.sectionLabel}>Fale conosco</h2>
        {isDesktop ? (
          <p style={{ ...styles.contactDescription, color: tokens.colors.brand.gray.DEFAULT }}>
            Para solicitações de troca, devolução, cancelamento, status do pedido ou cadastro.
            <br />
            Entre em contato com nossa Central de Atendimento:
          </p>
        ) : (
          <p style={{ ...styles.contactDescription, color: tokens.colors.brand.gray.light150 }}>
            Para solicitações de troca, devolução, cancelamento, status do pedido ou cadastro, entre
            em contato com nossa Central de Atendimento:
          </p>
        )}
        <div style={styles.phoneRow}>
          <PhoneIcon
            width={isDesktop ? 24 : 16}
            height={isDesktop ? 24 : 16}
            aria-hidden="true"
            focusable="false"
          />
          <p style={styles.phoneLabel}>Telefone</p>
          <p style={styles.phoneNumber}>
            <span>•</span>
            <span>{SUPPORT_PHONE_NUMBER}</span>
          </p>
        </div>
      </section>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.large,
    width: '100%',
    padding: `${tokens.spacing.default} ${tokens.spacing.pageGutter}`,
    paddingBottom: tokens.spacing.large,
    boxSizing: 'border-box',
  },
  categorySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.default,
  },
  eyebrow: {
    margin: 0,
    fontSize: tokens.typography.fontSize.eyebrow,
    fontWeight: tokens.typography.fontWeight.semiBold,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.brand.gray.DEFAULT,
    textTransform: 'uppercase',
  },
  heading: {
    margin: 0,
    fontSize: tokens.typography.fontSize.heading,
    fontWeight: tokens.typography.fontWeight.semiBold,
    lineHeight: tokens.typography.lineHeight.heading,
    color: tokens.colors.global.black,
  },
  categoryList: {
    display: 'flex',
    gap: tokens.spacing.small,
    justifyContent: 'space-between',
  },
  contactContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.small,
  },
  contactContainerDesktop: {
    gap: tokens.spacing.default,
    padding: tokens.spacing.medium,
    borderRadius: tokens.radius.small,
    alignSelf: 'flex-start',
  },
  sectionLabel: {
    margin: 0,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: tokens.typography.fontWeight.semiBold,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.brand.gray.DEFAULT,
    textTransform: 'uppercase',
  },
  contactDescription: {
    margin: 0,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.regular,
    lineHeight: tokens.typography.lineHeight.body,
  },
  phoneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing.small,
  },
  phoneLabel: {
    margin: 0,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.brand.gray.DEFAULT,
  },
  phoneNumber: {
    margin: 0,
    display: 'flex',
    gap: tokens.spacing.tiny,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.brand.gray.DEFAULT,
  },
};
